// The 'object' outline provider: a stage shape (`path`, `rect`, `circle`, `brush`) resolved to an outline in its OWN space plus the transform that places it on stage (DECISIONS 2026-10-07 "object outlines bake in shape-local space"). Automation and fades move shapes every frame; a key over local geometry survives that, so the baked field does too. Only a large world-scale change (a power-of-two bucket) rebakes.
import { localPivot, resolveWorldTransform } from '~/lib/objectBounds'
import { rawPathToString, stringToRawPath, transformRawPath } from '~/lib/svg/paths'
import { anchorsOf } from './path'

/**
 * Types whose outline is their `data`, filled by PixiShape. `brush` included: its `data` is the stroke's closed outline (the offset centerline with caps), which is what paints — the centerline alone encloses nothing. A legacy non-`variableWidth` brush is filled from `data` the same way, so it resolves the same way.
 */
const SHAPE_TYPES = new Set(['path', 'rect', 'circle', 'brush'])

/** Bucket range: the field is baked at 2^bucket × the shape's local size. */
export const MIN_SCALE_BUCKET = -6
export const MAX_SCALE_BUCKET = 6

/** `bakeField`'s default `maxSize` (what `fieldFor` bakes at): once `d` spans this, a larger bucket bakes the same grid, so it is not a new bucket. */
const FIELD_SIZE = 512

// Local outlines held by `data` string, so a fresh store item (a fade's or an automation's copy) with the same geometry skips the parse.
const LOCAL_CAP = 256

/**
 * The scale bucket of a world scale: `round(log2(max(|sx|, |sy|)))`, clamped to [MIN, MAX], and capped where `extent · 2^bucket` reaches the field's size cap (beyond it every bucket bakes the same grid). A zero or non-finite scale takes MIN.
 * @param {number} sx @param {number} sy world scale (signed)
 * @param {number} [extent] the local geometry's long side; omitted, no cap
 */
export const scaleBucket = (sx, sy, extent = 0) => {
	const m = Math.max(Math.abs(sx), Math.abs(sy))
	const k = m > 0 && Number.isFinite(m) ? Math.max(MIN_SCALE_BUCKET, Math.round(Math.log2(m))) : MIN_SCALE_BUCKET
	const cap = extent > 0 ? Math.ceil(Math.log2(FIELD_SIZE / extent)) : Number.POSITIVE_INFINITY
	return Math.min(k, MAX_SCALE_BUCKET, cap) || 0 // no −0 from ceil/round
}

/** `[a, b, c, d, tx, ty]` applied to a point: `(a·x + c·y + tx, b·x + d·y + ty)`, the SVG/Pixi column convention. */
export const applyTransform = ([a, b, c, d, tx, ty], [x, y]) => [a * x + c * y + tx, b * x + d * y + ty]

/** A transform as an SVG `transform` attribute; undefined for none. */
export const svgMatrix = t => (t ? `matrix(${t.join(' ')})` : undefined)

/** Stage units per field (outline-space) unit along the shorter axis: a field distance `r` reads `r × stageDistanceScale(t)` on stage — exact for translate, rotate and uniform scale, conservative under non-uniform scale. 1 for an absent transform. */
export const stageDistanceScale = t => (t ? Math.min(Math.hypot(t[0], t[1]), Math.hypot(t[2], t[3])) : 1)

// Live objects by id: the store's record, or a Map.
const lookup = objects => (objects instanceof Map ? id => objects.get(id) : id => objects?.[id])

// data → { extent, byBucket: Map<bucket, outline | null> }; insertion-ordered, oldest dropped past LOCAL_CAP.
const locals = new Map()

const localEntry = data => {
	let entry = locals.get(data)
	if (entry) return entry
	const raw = stringToRawPath(data)
	let extent = 0
	if (raw?.length) {
		// Control-point bounds contain the curve, so for a strongly curved shape the size cap can come a bucket early (one fewer rebake, a field up to 2× coarser near the cap), never late.
		let minX = Number.POSITIVE_INFINITY
		let minY = Number.POSITIVE_INFINITY
		let maxX = Number.NEGATIVE_INFINITY
		let maxY = Number.NEGATIVE_INFINITY
		for (const seg of raw)
			for (let i = 0; i < seg.length; i += 2) {
				if (seg[i] < minX) minX = seg[i]
				if (seg[i] > maxX) maxX = seg[i]
				if (seg[i + 1] < minY) minY = seg[i + 1]
				if (seg[i + 1] > maxY) maxY = seg[i + 1]
			}
		if (Number.isFinite(maxX - minX) && Number.isFinite(maxY - minY)) extent = Math.max(maxX - minX, maxY - minY)
	}
	entry = { extent, byBucket: new Map() }
	if (locals.size >= LOCAL_CAP) locals.delete(locals.keys().next().value)
	locals.set(data, entry)
	return entry
}

// The shape's `data` scaled by 2^bucket (exact in floating point), as `toOutline(d, anchors)` builds it: `d` keeps every subpath, rounded to 0.1 by `rawPathToString`; anchors are the first subpath's, in that same space.
const localOutline = (data, entry, bucket, toOutline) => {
	if (entry.byBucket.has(bucket)) return entry.byBucket.get(bucket)
	let out = null
	const raw = stringToRawPath(data)
	if (raw?.length) {
		const p = 2 ** bucket
		if (p !== 1) transformRawPath(raw, p, 0, 0, p, 0, 0)
		const d = rawPathToString(raw).trim()
		const anchors = anchorsOf(d)
		if (anchors.length) out = toOutline(d, anchors)
	}
	entry.byBucket.set(bucket, out)
	return out
}

// Final results per store item, re-made only when its world transform (its own or an ancestor's) changes.
const resolved = new WeakMap()

/**
 * The outline of live object `id`: `{ d, key, anchors, segments, transform }`, or null when the object is missing, is not a shape, or holds no geometry.
 * - `d`, `key`, `segments`: the shape's local `data` scaled by `2^bucket` (see `scaleBucket`), built by `toOutline` — so `key` is `outlineKey(d)`, and the bucket reaches the bake through `d` with `bakeField(d)` unchanged. Translate, rotate and a scale change inside one bucket keep the key; two copies of one shape share it.
 * - `transform`: `[a, b, c, d, tx, ty]`, outline (field) space → stage: the world transform (`resolveWorldTransform`, every group ancestor, signed scale) with `1 / 2^bucket` folded into its linear part.
 * - `anchors`: STAGE space (the outline's anchors through `transform`), so linear frames read them as for a surface.
 */
export const resolveObjectOutline = (id, objects, toOutline) => {
	if (id == null || !objects) return null
	const get = lookup(objects)
	const obj = get(id)
	if (!obj || typeof obj !== 'object' || !SHAPE_TYPES.has(obj.type)) return null
	if (typeof obj.data !== 'string' || !obj.data) return null

	const { x, y, scaleX, scaleY, rotation } = resolveWorldTransform(obj, { get })
	if (![x, y, scaleX, scaleY, rotation].every(Number.isFinite)) return null
	const sig = `${x},${y},${scaleX},${scaleY},${rotation}`
	const hit = resolved.get(obj)
	if (hit?.sig === sig) return hit.out

	const entry = localEntry(obj.data)
	const bucket = scaleBucket(scaleX, scaleY, entry.extent)
	const local = localOutline(obj.data, entry, bucket, toOutline)
	let out = null
	if (local) {
		const rad = (rotation * Math.PI) / 180
		const cos = Math.cos(rad)
		const sin = Math.sin(rad)
		const inv = 2 ** -bucket
		// Pixi's local matrix: translate · rotate · scale · translate(−pivot), the signed scale carrying a mirror; then undo the bucket's pre-scale. `x`/`y` is where the pivot lands.
		const pv = localPivot(obj)
		const a = scaleX * cos,
			b = scaleX * sin,
			c = -scaleY * sin,
			dd = scaleY * cos
		const transform = [a * inv, b * inv, c * inv, dd * inv, x - a * pv.x - c * pv.y, y - b * pv.x - dd * pv.y]
		out = { ...local, anchors: local.anchors.map(pt => applyTransform(transform, pt)), transform }
	}
	resolved.set(obj, { sig, out })
	return out
}
